export type PaymentSourceValue = 'Revolut Credit Card' | 'PayPal' | 'Braintree' | 'Google' | 'Apple' | 'Bank transfer';
export type StatusFilter = 'all' | 'linked' | 'incomplete' | 'force_closed' | 'not linked' | 'overpaid' | 'underpaid' | 'expected';
/**
 * 'vasco_closed' is derived by the payments list route (SW-2486): a reconciled
 * payment closed by directly claimed Vasco invoices alone, with no linked
 * orders. Rendered "Completed" like force_closed.
 * 'item_force_closed' is derived the same way (SW-2489): a reconciled payment
 * that needed at least one item-level force close (a force-closed order link
 * or a resolved unlinked settlement). Also rendered "Completed".
 */
export type LinkedStatus = 'not linked' | 'auto' | 'manual' | 'overpaid' | 'underpaid' | 'expected' | 'duplicate' | 'partial' | 'force_closed' | 'vasco_closed' | 'item_force_closed';
/** Vasco coverage of a payment's linked orders: all / some / none carry a Vasco invoice. */
export type VascoLinkStatus = 'linked' | 'partial' | 'not linked';
export type TxType = 'CRDT' | 'DBIT';
/** B2B orders list filter — one linkage leg at a time, matching the PAYMENT and VASCO columns. */
export type LinkedFilter = 'all' | 'linked' | 'not-linked' | 'force-closed' | 'overpaid' | 'vasco-linked' | 'vasco-not-linked';
/** Vasco invoices list filter — one linkage leg at a time, matching the ORDER and PAYMENT columns. */
export type VascoLinkedFilter = 'all' | 'order-linked' | 'order-not-linked' | 'payment-linked' | 'payment-not-linked';

export interface Payment {
  id: number;
  acctSvcrRef: string;
  date: string;
  amount: number;
  currency: string;
  type: TxType;
  counterparty: string | null;
  description: string | null;
  remittanceInfo: string | null;
  linkedOrderId: number | null;
  linkedOrderIds: number[];
  linkedOrderNumber: string | null;
  linkedOrderNumbers: string[];
  /** Vasco invoice numbers directly linked via payment_vasco_links (SW-2486). */
  linkedVascoNumbers?: string[];
  linkedStatus: LinkedStatus;
  vascoStatus: VascoLinkStatus;
  paymentSource: PaymentSourceValue | null;
  reconciliationNote?: string | null;
  /** Stored Vasco GK status (server/utils/gkPaymentStatus.js); null when it could not be loaded. */
  gk?: PaymentGkStatus | null;
}

/**
 * The GK column of the payments list:
 *   none      not part of the GK export (before the start date, other currency, pending settlement)
 *   unknown   part of it, but not calculated yet
 *   ready     no B code of its own (dayHeld: another payment or the statement holds its day)
 *   blocked   its own B codes
 *   exported  an active GK export holds it
 */
export type PaymentGkStatus =
  | { state: 'none' }
  | { state: 'unknown'; reason?: 'export_cancelled' }
  | { state: 'exported'; dokument: string }
  | {
      state: 'ready' | 'blocked';
      codes: string[];
      exceptions: PaymentGkException[];
      dayHeld: boolean;
      dayCodes: string[];
      heldBy: number[];
      day: string;
      computedAt: string;
    };

/** One B code of a payment. orderNumber names the order also for a refund transaction item. */
export interface PaymentGkException {
  code: string;
  itemKey: string | null;
  orderNumber: string | null;
  /** All orders of the item, when it is one card charge that pays several orders. */
  orderNumbers?: string[] | null;
  /** B2: the partner that has no receivable konto (not in statuses calculated before this field existed). */
  partnerSifra?: number | null;
  message: string;
  help: string | null;
}

/** GET /api/accounting/gk-export/payment-status, also in the payments list response. */
export interface GkStatusMeta {
  /** Data changed after the last calculation; the job recalculates soon. */
  pending: boolean;
  computedAt: string | null;
  durationMs: number | null;
  error: string | null;
  errorAt: string | null;
  /** False while the job is switched off on the server (GK_STATUS_JOB_DISABLED). */
  jobEnabled?: boolean;
  /** The stored status decides the tabs Incomplete / Linked / Completed (export on and job running). */
  decidesTabs?: boolean;
}

export interface B2BOrder {
  id: number;
  woo_order_id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  order_date: string;
  status: string;
  total: string;
  currency: string;
  is_device_order: boolean;
  payment_method: string | null;
  billing_company: string | null;
  billing_first_name: string | null;
  billing_last_name: string | null;
  /**
   * 'overpaid' = two or more payments together pay more than the total (double charge or wrong link),
   * 'full' = fully covered, 'partial' = at least one payment but not yet fully paid, 'none' = no payments,
   * 'force_closed' = no payments, and accounting force closed the order (migration 0120)
   */
  is_linked: 'overpaid' | 'full' | 'partial' | 'none' | 'force_closed';
  /** Why the order was force closed. Kept after a reopen, so read it only when is_linked is 'force_closed'. */
  force_close_note: string | null;
  /** A vasco_invoices row with this order's number exists. */
  has_vasco: boolean;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
}

export interface StatsBucket {
  total: number;
  linked: number;
  notLinked: number;
  overpaid: number;
  underpaid: number;
  expected: number;
  duplicate?: number;
  forceClosed?: number;
  /** Linked or completed by the links, but with a GK B code, so in "Incomplete". */
  gkBlocked?: number;
}

export interface StatsInfo {
  page: StatsBucket;
  last30Days: StatsBucket;
  lastYear: StatsBucket;
}

export interface PaymentsResponse {
  payments: Payment[];
  pagination: PaginationInfo;
  stats: StatsInfo;
  gkStatus?: GkStatusMeta | null;
}

/**
 * A new link after which all payments of an order together pay more than the
 * order total: a double charge or a wrong link. Payments that together pay the
 * total (a split payment, SW-2410) are not reported.
 */
export interface LinkedToOtherPayments {
  paymentId: number;
  orderNumber: string | null;
  otherPaymentIds: number[];
  orderTotal?: number | null;
  currency?: string | null;
  /** How much all payments of the order pay over its total. */
  overpaidBy?: number;
}

/** A matched payment whose recomputed status is not 'auto'. */
export interface PaymentDiscrepancy {
  paymentId: number;
  amount: number;
  currency: string;
  status: string;
}

export interface MatchResult {
  matched: number;
  ordersLinked: number;
  skipped: number;
  alreadyLinked: number;
  conflictingLinks?: number;
  /** Bank transfers that found no unique order. */
  skippedPaymentIds?: number[];
  paymentsWithDiscrepancy?: PaymentDiscrepancy[];
  dryRun: boolean;
}

// Shape of GET /api/accounting/revolut-api/status (server/utils/revolutSync.js).
export interface RevolutApiStatus {
  configured: boolean;
  enabled: boolean;
  missing: string[];
  lastImport: { finishedAt: string; imported: number; skipped: number } | null;
}

/**
 * A pending PayPal payment ("stub") checked against the new bank credits
 * (server/utils/bankPaymentImport.js). ambiguous: more than one bank credit
 * fits, so nothing was merged.
 */
export interface PaypalSettlementCheck {
  stubId: number;
  currency: string;
  expectedAmount: number;
  matched: boolean;
  ambiguous?: boolean;
  candidatePaymentIds?: number[];
  realPaymentId?: number;
  actualAmount?: number;
  settlementFee?: number;
}

// Shape of POST /api/accounting/upload (camt.053 XML).
export interface BankPaymentUploadResult {
  imported: number;
  skipped: number;
  paypalSettlements?: PaypalSettlementCheck[];
  vascoLinksAutoCreated?: number;
}

// Shape of POST /api/accounting/revolut-api/import.
export interface RevolutApiImportResult {
  imported: number;
  skipped: number;
  paypalSettlements?: PaypalSettlementCheck[];
  from: string;
  to: string;
  fetched: number;
  pendingCredits: number;
}

// Shape of POST /api/accounting/revolut-reconcile response. Keep field names
// aligned with the stats object of reconcileRevolutStatement in
// server/utils/revolutMerchantReconcile.js.
export interface ReconcileResult {
  slices: number;
  payouts: number;
  payoutsMatched: number;
  /** Payouts whose bank payment was found by the payout's Transaction ID. */
  payoutsMatchedByReference?: number;
  payoutsUnmatched: number;
  /** `date` is the time the payout completed. */
  unmatchedPayouts?: Array<{ transactionId: string; amount: number; currency: string; date: string }>;
  /** Payments whose card transactions this import moved to another payout's payment. */
  paymentsWithMovedRows?: Array<{ paymentId: number; rows: number }>;
  payoutsAmbiguous?: number;
  payoutsDisambiguatedByDate?: number;
  salesLinked: number;
  salesAlreadyLinked: number;
  salesConflictingLink?: number;
  linkedToOtherPayments?: LinkedToOtherPayments[];
  salesOrderNotFound: number;
  salesNoOrderRef?: number;
  salesCurrencyMismatch?: number;
  refundsRecorded?: number;
  refundsOrderNotFound?: number;
  refundFundingTransfersRecorded?: number;
  revolutTransactionsInserted?: number;
  revolutTransactionsUpdated?: number;
  orderTotalMismatches?: number;
  /** Orders settled more than once inside one payout (possible duplicate charge). */
  linksWithMultipleSettlements?: number;
  balanceMismatches?: number;
  chainsWithCarryover?: number;
  trailingRows?: number;
  trailingRowsPersisted?: number;
  linkOrderFallbacksUsed?: number;
  paymentsReconciled?: number;
  paymentsWithDiscrepancy?: number;
  ambiguousPayouts?: Array<{
    transactionId: string;
    amount: number;
    currency: string;
    dateFrom: string;
    dateTo: string;
    candidatePaymentIds: number[];
  }>;
  overwrite?: boolean;
  overwriteDateFrom?: string | null;
  overwriteDateTo?: string | null;
  priorLinksWiped?: number;
  priorTransactionsWiped?: number;
  priorPaymentsReset?: number;
  dryRun: boolean;
}

// Shape of GET /api/accounting/revolut-merchant-api/status (server/utils/revolutMerchantSync.js).
export interface RevolutMerchantApiStatus {
  configured: boolean;
  enabled: boolean;
  missing: string[];
  sandbox: boolean;
  /** Revolut is still preparing the report of the last import. The next import continues it. */
  waiting: boolean;
  lastImport: { finishedAt: string; payoutsMatched: number; salesLinked: number; notes: string[] } | null;
}

// Shape of POST /api/accounting/revolut-merchant-api/import. While Revolut is
// still preparing the report (HTTP 202), only waiting, from, to and notes are set.
// from and to are null when there was nothing to import (no report was requested).
export interface RevolutMerchantImportResult extends ReconcileResult {
  waiting?: boolean;
  from?: string | null;
  to?: string | null;
  rowsFetched?: number;
  rowsProcessed?: number;
  rowsBeforeBoundary?: number;
  sandbox?: boolean;
  /** Plain sentences about payouts that need attention. */
  notes?: string[];
  payoutsSkipped?: number;
  payoutsWaiting?: number;
  payoutsUnbalanced?: number;
}

/** POST /api/accounting/gk-export/rates/sync, the "USD rates" step of Sync. */
export interface GkRatesSyncResult {
  /** True while the GK export is switched off on the server. */
  skipped: boolean;
  /** False when the rates were already fetched up to today. */
  fetched?: boolean;
  newestRate?: { date: string; rate: string } | null;
  /** Earlier USD payment days that had no rate and were fetched too. */
  earlierDays?: number;
  earlierDaysError?: string | null;
}

export interface BraintreePaymentAssignment {
  paymentId: number;
  amount: number;
  currency: string;
  rowCount: number;
  ordersLinked: number;
  settlementDateFrom: string | null;
  settlementDateTo: string | null;
}

/**
 * Why a Braintree bank credit found no AIB settlements that add up to it.
 *   report_too_short: the AIB data ends before the payment date.
 *   no_settlements:   no AIB settlements in the payment's date window.
 *   amount_mismatch:  settlements exist, but no run of days adds up to the amount.
 */
export type BraintreeUnmatchedReason = 'report_too_short' | 'no_settlements' | 'amount_mismatch';

export interface BraintreeUnmatchedPayment {
  paymentId: number;
  amount: number;
  currency: string;
  /** YYYY-MM-DD */
  paymentDate: string;
  reason: BraintreeUnmatchedReason;
  /** Latest settlement date in the AIB data for this currency (YYYY-MM-DD). */
  latestSettlementDate: string | null;
  windowStart: string;
  windowEnd: string;
  closestRun: {
    settlementDateFrom: string;
    settlementDateTo: string;
    sum: number;
    /** Positive when the settlements are short of the payment amount. */
    shortBy: number;
  } | null;
}

export interface BraintreeReconcileResult {
  paymentsMatched: number;
  paymentsUnmatched: number;
  paymentsAmbiguous?: number;
  /** Matched payments whose recomputed status is not 'auto' (the amounts do not add up). */
  paymentsWithDiscrepancy?: number;
  /** Matched payments with sale rows whose order could not be linked. null = no order number in the report. */
  paymentsWithMissingOrders?: Array<{ paymentId: number; amount: number; currency: string; orderNumbers: Array<string | null> }>;
  linkedToOtherPayments?: LinkedToOtherPayments[];
  unmatchedPayments?: BraintreeUnmatchedPayment[];
  /** Rows of this report that no payment claimed yet (they wait for a later payout). */
  reportRowsUnclaimed?: number;
  /** Latest settlement date in the uploaded report (YYYY-MM-DD). */
  reportSettlementDateTo?: string | null;
  paymentAssignments?: BraintreePaymentAssignment[];
  ordersLinked: number;
  ordersNotFound: number;
  ordersAlreadyLinked: number;
  ordersConflictingLink?: number;
  ordersCurrencyMismatch?: number;
  unclaimedRows: number;
  rowsParsed?: number;
  rowsPersisted?: number;
  rowsUpdated?: number;
  salesParsed?: number;
  refundsParsed?: number;
  /** AIB chargeback debits and handling fees; they settle no card transaction. */
  feeOnlyParsed?: number;
  /** Signed sum of those rows — negative, because AIB deducts them from the payout. */
  feeOnlyNet?: number;
  /** Adjustment-shaped rows with no date, currency or amount; dropped. */
  rowsUnusable?: number;
  /** CSV Transaction IDs seen more than once. Each one overwrote another row on upsert. */
  duplicateTransactionIds?: string[];
  orderTotalMismatches?: number;
  rowsWithCurrencyMismatch?: number;
  overwrite?: boolean;
  overwriteDateFrom?: string | null;
  overwriteDateTo?: string | null;
  priorLinksWiped?: number;
  priorTransactionsWiped?: number;
  priorPaymentsReset?: number;
  dryRun: boolean;
}

export interface PaypalCurrencyResult {
  ordersLinked: number;
  ordersNotFound: number;
  refundsApplied: number;
  alreadyLinked: number;
  conflictingLinks?: number;
  linkedToOtherPayments?: LinkedToOtherPayments[];
  ordersCurrencyMismatch?: number;
  totalGross: number;
  totalFees: number;
  totalNet: number;
  stubPaymentId: number | null;
}

export interface PaypalReconcileResult {
  currencies: Record<string, PaypalCurrencyResult>;
  skipped: number;
  totalTransactions: number;
  relevantTransactions: number;
}

export interface VascoReconcileResult {
  rowsParsed: number;
  uniqueOrders: number;
  ordersUpdated: number;
  ordersNotFound: number;
  creditNotesStored: number;
  invoicesStored: number;
  invoicesFailed: number;
  /** Google/Apple payouts linked to their order-less invoice by amount */
  vascoLinksAutoCreated?: number;
  /** Google/Apple payouts with more than one candidate invoice; left unlinked. */
  vascoLinksAmbiguous?: number;
  skippedBlank: number;
}

// Shape of GET /api/accounting/vasco-invoices rows. Keep field names aligned
// with the route response in server/routes/accounting.js.
export interface VascoInvoice {
  id: number;
  /** Vasco document number "RACUN/LETO", e.g. "3814/2026" */
  vascoInvoice: string;
  invoiceSeq: number;
  invoiceYear: number;
  invoiceDate: string | null;
  orderNumber: string | null;
  buyer: string | null;
  recipient: string | null;
  amount: number | null;
  paymentMethod: string | null;
  isCreditNote: boolean;
  wooOrderId: number | null;
  linkedToOrder: boolean;
  linkedToPayment: boolean;
  /** Payments linked directly to this invoice via payment_vasco_links (SW-2486). */
  directPaymentCount?: number;
}

// Shape of a payment_vasco_links row as returned by the payment detail and
// link/unlink endpoints. Keep field names aligned with
// fetchLinkedVascoInvoices in server/utils/paymentLinks.js.
export interface VascoLink {
  id: number;
  vascoInvoiceId: number | null;
  /** Vasco document number "RACUN/LETO", e.g. "3814/2026" */
  invoiceNumber: string;
  /** Claimed portion in payment currency; null = full invoice amount. */
  amount: string | null;
  /** Vasco invoice amount (EUR); null while the link is unverified. */
  invoiceAmount: string | null;
  verified: boolean;
  isCreditNote: boolean;
  buyer: string | null;
  recipient: string | null;
  invoiceDate: string | null;
  orderNumber: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface VascoStatsBucket {
  total: number;
  linkedOrder: number;
  linkedPayment: number;
}

export interface VascoInvoicesResponse {
  invoices: VascoInvoice[];
  pagination: PaginationInfo;
  stats: {
    page: VascoStatsBucket;
    thisYear: VascoStatsBucket;
    allTime: VascoStatsBucket;
  };
}

// ─── B2B order detail (/accounting/b2b-orders/:id) ──────────────────────────
// Keep field names aligned with GET /api/accounting/b2b-orders/:id in
// server/routes/accounting.js.

export interface B2BOrderDetailOrder {
  id: number;
  wooOrderId: number;
  orderNumber: string;
  invoiceNumber: string | null;
  customerName: string | null;
  customerEmail: string | null;
  orderDate: string;
  status: string | null;
  total: string;
  currency: string;
  isDeviceOrder: boolean;
  /** Vasco invoice number written by the import onto the order itself. */
  vascoInvoice: string | null;
  paymentMethod: string | null;
  billingCompany: string | null;
  billingFirstName: string | null;
  billingLastName: string | null;
  /** Non-null = force closed now (migration 0120). */
  forceClosedAt: string | null;
  /** Kept after a reopen, so read it only when forceClosedAt is set. */
  forceCloseNote: string | null;
  forceClosedByName: string | null;
}

/** A payment linked to the order, with the claim it holds on that order. */
export interface OrderLinkedPayment {
  id: number;
  acctSvcrRef: string;
  date: string;
  amount: string;
  currency: string;
  counterparty: string | null;
  description: string | null;
  remittanceInfo: string | null;
  paymentSource: PaymentSourceValue | null;
  linkedStatus: LinkedStatus;
  /** Explicit share of the order this payment covers; null = full order total. */
  partialAmount: string | null;
  /** partialAmount, falling back to the order total. */
  claimedAmount: string | null;
  processingFee: string | null;
  settlementAmount: string | null;
  linkedAt: string;
  forceClosedAt: string | null;
  forceCloseNote: string | null;
  forceClosedByName: string | null;
}

/**
 * A Vasco document shown on the order. 'import' rows come from the Vasco export
 * matching the order number and cannot be removed here; 'manual' rows are
 * order_vasco_links and can.
 */
export interface OrderVascoDocument {
  source: 'import' | 'manual';
  /** order_vasco_links.id — null for imported rows. */
  linkId: number | null;
  invoiceId: number | null;
  invoiceNumber: string;
  invoiceDate: string | null;
  buyer: string | null;
  recipient: string | null;
  amount: string | null;
  paymentMethod: string | null;
  isCreditNote: boolean;
  /** The number exists in the Vasco import. Always true for imported rows. */
  verified: boolean;
  /** Order number the imported document itself carries, when it differs. */
  invoiceOrderNumber?: string | null;
  createdAt: string | null;
  createdByName: string | null;
  /** The order this document belongs to — set on every row by the server. */
  linkedOrderId?: number;
  linkedOrderNumber?: string;
}

export interface OrderCoverage {
  total: number;
  covered: number;
  /** Negative when the order is overpaid. */
  remaining: number;
  status: 'overpaid' | 'full' | 'partial' | 'none';
}

export interface B2BOrderDetailData {
  order: B2BOrderDetailOrder;
  payments: OrderLinkedPayment[];
  coverage: OrderCoverage;
  vascoDocuments: OrderVascoDocument[];
  creditNotes: Array<{ number: string; amount: string }>;
}

/** A row from GET /api/accounting/b2b-orders/:id/payment-search. */
export interface OrderPaymentCandidate {
  id: number;
  acctSvcrRef: string;
  date: string;
  amount: string;
  currency: string;
  counterparty: string | null;
  description: string | null;
  remittanceInfo: string | null;
  paymentSource: PaymentSourceValue | null;
  linkedStatus: LinkedStatus;
  linkedOrderCount: number;
}

export function getWooCommerceOrderUrl(wooOrderId: number): string {
  return `https://b2b.naviter.com/wp-admin/post.php?post=${wooOrderId}&action=edit`;
}

// --- Vasco GK export (GK_EXPORT_PLAN.md) ------------------------------------

export interface GkStatus {
  enabled: boolean;
  startDate: string;
  closedThrough: string | null;
  lastUpload: { id: number; filename: string; file_kind: 'gk_export' | 'faw'; exported_at: string; uploaded_at: string; max_posting_date: string | null } | null;
  partners: number;
  postings: number;
  newestUsdRate: string | null;
  lastExports?: Record<string, { id: number; date_from: string; date_to: string; finished_at: string }>;
  drift?: { checkedAt: string; changedDocuments: number } | null;
}

export interface GkUploadReport {
  encoding: string;
  lines: number;
  postingDates: { from: string | null; to: string | null };
  partners: {
    inFile: number;
    new: number;
    changed: Array<{ sifra: number; naziv1: string | null }>;
    keptNewer: number[];
    unchanged: number;
  };
  invalidBlocks: Array<{ sifra: number; reason: string }>;
  partnerVariantsInFile: number[];
  postings: { inFile: number; new: number; updated: number; keptNewer: number; conflicts: Array<{ key: string; reason: string }> };
  letoVezaDiffers: Array<{ invoice: string; gkVeza: string; gkLetoVeza: number }>;
  externalReceipts: { inFile: number };
  invoicesWithoutPosting: { count: number; first: string[] } | null;
}

export interface GkUpload {
  id: number;
  filename: string;
  file_kind: 'gk_export' | 'faw';
  exported_at: string;
  uploaded_at: string;
  line_count: number | null;
  min_posting_date: string | null;
  max_posting_date: string | null;
  report: GkUploadReport | null;
  uploaded_by_name: string | null;
}

export interface GkPartnerKontoRow {
  partner_sifra: number;
  konto: string;
  sifra_valute_rule: 'none' | 'order_currency';
  source: 'seed' | 'derived' | 'manual';
  note: string | null;
  updated_at: string;
  naziv1: string | null;
}

export interface GkRateDay {
  day: string;
  rate: string | null;
  rateDate?: string;
  source?: 'bsi' | 'manual' | 'evasco';
  fromEarlierDay?: boolean;
}

export type GkCurrency = 'EUR' | 'USD';

export interface GkPreviewLine {
  type: string;
  konto: string;
  side: 'D' | 'K';
  amountCents: number;
  pcCents?: number;
  partnerSifra: number | null;
  partnerName: string | null;
  veza: string | null;
  letoVeza: number | null;
  currency?: { sifra: number; amountCents: number } | null;
  opis: string;
  paymentId?: number | null;
  orderNumber: string | null;
}

export interface GkControlRow {
  statementDay: string;
  dokument: string;
  status: 'ok' | 'partial' | 'mismatch' | 'no_statement';
  appCreditsCents: number;
  appCreditCount: number;
  statementCreditsCents: number | null;
  statementCreditCount: number | null;
  statementComplete: boolean | null;
  bankCents: number;
  debitCents: number;
  creditCents: number;
  fxCents: number;
}

export interface GkPreviewDocument {
  status: 'ready' | 'held';
  simbol: number;
  dokument: string;
  statementDay: string;
  obdobje: { mesec: number; leto: number };
  periodMoved: boolean;
  kind: string;
  rate: string | null;
  rateDate: string | null;
  debitCents: number | null;
  creditCents: number | null;
  bankCents: number | null;
  bankCurrencyCents: number | null;
  fxCents: number;
  paymentIds: number[];
  exceptionCodes: string[];
  control: GkControlRow | null;
  lines: GkPreviewLine[];
}

export interface GkFinding {
  code: string;
  level: 'block' | 'warning';
  paymentId: number | null;
  day: string | null;
  itemKey: string | null;
  orderNumber: string | null;
  message: string;
  help: string | null;
}

export interface GkPreview {
  currency: GkCurrency;
  from: string;
  to: string;
  settings: { startDate: string; closedThrough: string | null; paypalEurBreakdownSymbol: number | null };
  rateFetch: { days: string[]; stored: number; coveredTo: string | null; error: string | null } | null;
  statementFetch: { days: string[]; entries: number; newEntries: number; error: string | null } | null;
  summary: {
    ready: number;
    held: number;
    exportedPayments: number;
    bankReadyCents: number;
    bankHeldCents: number;
    bankReadyCurrencyCents: number | null;
    paymentsInRange: number;
    creditsCents: number;
    exceptionsByCode: Record<string, number>;
    warningsByCode: Record<string, number>;
  };
  documents: GkPreviewDocument[];
  exceptions: GkFinding[];
  warnings: GkFinding[];
  control: GkControlRow[];
  paypalBreakdowns: Array<{ paymentId: number; day: string; symbol: number | null; lines: GkPreviewLine[]; exceptions: GkFinding[]; warnings: GkFinding[] }>;
}

export interface GkStatementReport {
  statements: Array<{
    fileId: number;
    currency: GkCurrency;
    from: string;
    to: string;
    openingCents: number | null;
    closingCents: number | null;
    entries: number;
    newEntries: number;
    pendingSkipped: number;
    credits: { count: number; cents: number };
    debits: { count: number; cents: number };
    days: { complete: number; partial: string[] };
  }>;
  skipped: Array<{ currency: string; entries: number }>;
}

export interface GkStatementFile {
  id: number;
  filename: string;
  currency: GkCurrency;
  iban: string;
  from_ts: string;
  to_ts: string;
  opening_cents: number | null; // null: the balance is not known
  closing_cents: number | null;
  loaded_at: string;
  loaded_by_name: string | null;
  new_entries: number;
}

export interface GkExportBatch {
  id: number;
  kind: string;
  currency: GkCurrency;
  date_from: string;
  date_to: string;
  status: 'running' | 'success' | 'failed' | 'cancelled';
  started_at: string;
  finished_at: string | null;
  file_name: string | null;
  summary_name: string | null;
  error: string | null;
  statement_control_overridden: boolean;
  override_note: string | null;
  imported_confirmed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  cancel_note: string | null;
  created_by_name: string | null;
  confirmed_by_name: string | null;
  cancelled_by_name: string | null;
  documents: Array<{ id: number; simbol: number; dokument: string; statementDay: string; status: 'active' | 'cancelled' | 'reversed'; debitCents: number; bankCents: number | null }>;
}

export interface GkExportResult {
  batchId: number;
  fileName: string;
  documents: Array<{ id: number; dokument: string; statementDay: string; paymentIds: number[] }>;
  heldDays: Array<{ day: string; codes: string[] }>;
  warnings: GkFinding[];
}

/** What a GK decision is about (server describeItems), for the decisions table. */
export interface GkDecisionItem {
  kind: 'order' | 'transaction' | 'payment' | 'carryover' | string;
  orderNumber?: string | null;
  source?: 'Revolut' | 'Braintree' | 'PayPal';
  txnId?: string;
  isRefund?: boolean | null;
  txnAmount?: string | null;
  txnCurrency?: string | null;
  invoices: string[];
  creditNotes: string[];
}

export interface GkResolution {
  id: number;
  payment_id: number;
  item_key: string;
  item?: GkDecisionItem;
  action: 'advance' | 'book_konto' | 'use_invoice' | 'refund_to_invoice' | 'accept_suggestion';
  konto: string | null;
  partner_sifra: number | null;
  partner_name: string | null;
  invoice_seq: number | null;
  invoice_year: number | null;
  note: string;
  created_at: string;
  created_by_name: string | null;
  amount: string;
  currency: string;
  counterparty: string | null;
  day: string;
  exported: boolean;
}

export interface GkDrift {
  id: number;
  checkedAt: string;
  trigger: string;
  checkedDocuments: number;
  documents: Array<{
    id: number;
    dokument: string;
    simbol: number;
    day: string;
    currency: GkCurrency;
    batchId: number;
    settingsChanged: boolean;
    payments: Array<{ paymentId: number; reason: 'changed' | 'blocked' | 'missing' | 'moved'; detail?: string; findings?: GkFinding[] }>;
  }>;
}
